import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./PatientTable.css";

import { ROUTES } from "../../../../classes/constants/routeConstants";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import IconButton from "../../../../components/iconButton/IconButton";
import { TooltipOrientation } from "../../../../components/tooltip/Tooltip";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { useDateFormatter } from "../../../../hooks/useDateFormatter";
import {
  formatDisplayDate,
  parseTimestampField,
} from "../../../../classes/utils/DateUtils";
import PatientDeleteConfirmationModal from "../deletePatientModal/PatientDeleteConfirmationModal";

import { ButtonSize, IconSize } from "../../../../classes/enums/ButtonEnum";
import { useRouteState } from "../../../../hooks/useRouteState";
import {
  startProcess,
  stopProcess,
} from "../../../../store/slices/ApplicationSlice";

function PatientTable({ loading, resultsPatientFiltering, refreshPatients }) {
  const { params } = useRouteState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkPermissions } = useAuth();
  const { t } = useTranslation();
  const formatDate = useDateFormatter();
  const { showPopup } = useAppContext();

  const canDeletePatient = useMemo(
    () => checkPermissions(DataPermissions.DELETE_PATIENT),
    [checkPermissions]
  );

  const columns = [
    { tradId: "6", property: "first_name" },
    { tradId: "7", property: "last_name" },
    { tradId: "8", property: "birthday" },
    { tradId: "9", property: "gender" },
    { tradId: "18", property: "last_access" },
  ];

  const switchSelectedPatient = id => {
    navigate(ROUTES.PATIENT_DASHBOARD.replace(":patientId", id));
  };

  useEffect(() => {
    loading
      ? dispatch(startProcess(t("392")))
      : dispatch(stopProcess());
  }, [loading, dispatch]);

  const handleDeletePatient = patientObj => {
    showPopup(
      <PatientDeleteConfirmationModal
        patientData={{ ...patientObj }}
        onPatientDeleted={refreshPatients}
      />
    );
  };

  return (
    <table className="App-patient-table">
      <colgroup>
        <col style={{ width: "var(--col-1)" }} />
        <col style={{ width: "var(--col-2)" }} />
        <col style={{ width: "var(--col-3)" }} />
        <col style={{ width: "var(--col-4)" }} />
        <col style={{ width: "var(--col-5)" }} />
        <col style={{ width: "var(--col-actions)" }} />
      </colgroup>
      <tbody>
        {resultsPatientFiltering.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length + 1}
              className="App-patient-notfound-row"
            >
              {t("23")}
            </td>
          </tr>
        ) : (
          resultsPatientFiltering.map((patient, index) => (
            <React.Fragment key={index}>
              <tr
                className={`App-patient-row ${
                  patient._id === params.patientId ? "selected" : ""
                }`}
                onClick={() => switchSelectedPatient(patient._id)}
              >
                {columns.map((col, colIndex) => (
                  <td key={colIndex}>
                    <span className="App-text-medium">
                      {col.property === "last_access"
                        ? (() => {
                            const parsed = parseTimestampField(patient.last_access);
                            return parsed
                              ? formatDisplayDate(parsed, {
                                  format: "MMM dd yyyy",
                                  includeTime: true,
                                  timeFormat: "HH:mm",
                                })
                              : "-";
                          })()
                        : patient[col.property]
                        ? formatDate(patient[col.property], t("53"))
                        : "-"}
                    </span>
                  </td>
                ))}
                {canDeletePatient && (
                  <td style={{ textAlign: "right" }}>
                    <IconButton
                      title={t("55")}
                      tooltipOrientation={TooltipOrientation.Left}
                      className="App-patient-table-actions-icon"
                      icon={IconsEnum.DELETE}
                      onClick={e => {
                        e.stopPropagation();
                        handleDeletePatient(patient);
                      }}
                      iconSize={IconSize.MICRO}
                      size={ButtonSize.SMALL}
                      noBgColor
                    />
                  </td>
                )}
              </tr>
            </React.Fragment>
          ))
        )}
      </tbody>
    </table>
  );
}

export default PatientTable;
